// src/components/UpdatePassword.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, AlertCircle, CheckCircle } from 'lucide-react';
import axios from 'axios';

const UpdatePassword = () => {
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();

  const yellowGradient = 'linear-gradient(135deg, #F2C231 0%, #C69A32 100%)';

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccessMessage('');
    setErrorMessage('');
    setIsLoading(true);

    // Frontend validation
    if (formData.newPassword !== formData.confirmNewPassword) {
      setErrorMessage('La nuova password e la conferma non coincidono.');
      setIsLoading(false);
      return;
    }

    // Basic password strength check (can be more robust)
    if (formData.newPassword.length < 8 ||
      !/[A-Z]/.test(formData.newPassword) ||
      !/[a-z]/.test(formData.newPassword) ||
      !/[0-9]/.test(formData.newPassword) ||
      !/[!@#$%^&*(),.?":{}|<>]/.test(formData.newPassword)) {
      setErrorMessage("La nuova password deve contenere almeno 8 caratteri e includere una maiuscola, una minuscola, un numero e un carattere speciale.");
      setIsLoading(false);
      return;
    }


    try {
      // Get the JWT token from local storage
      const consent = localStorage.getItem('cookieConsent');
      let token;

      if (consent === 'true') {
        token = localStorage.getItem('jwtToken');
      } else {
        token = sessionStorage.getItem('jwtToken');
      }
      if (!token) {
        setErrorMessage('Token di autenticazione non trovato. Effettua l\'accesso.');
        setIsLoading(false);
        navigate('/login'); // Redirect to login if not authenticated
        return;
      }

      const config = {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` // Attach the token to the header
        }
      };

      // API call to your local backend
      const response = await axios.patch( // Use PATCH as it's an update operation
        'https://api.cafpraticheonline.it/api/auth/update-password', // Matches your backend route
        {
          old_password: formData.currentPassword, // Backend expects old_password
          new_password: formData.newPassword,     // Backend expects new_password
        },
        config
      );

      setSuccessMessage(response.data.message);
      setFormData({ // Clear the form fields on success
        currentPassword: '',
        newPassword: '',
        confirmNewPassword: '',
      });
    } catch (error) {
      console.error("Update password error:", error.response?.data || error.message);
      setErrorMessage(error.response?.data?.message || 'An unexpected error occurred.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div
            className="mx-auto h-16 w-16 rounded-full flex items-center justify-center mb-4"
            style={{ background: yellowGradient }}
          >
            <Lock className="h-8 w-8 text-white" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Aggiorna la tua password</h2>
          <p className="text-gray-600">Inserisci la password attuale e quella nuova</p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <input
                name="currentPassword"
                type="password"
                required
                value={formData.currentPassword}
                onChange={handleInputChange}
                className="block w-full px-3 py-3 border border-gray-300 rounded-lg placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                placeholder="Password attuale"
              />
            </div>
            <div>
              <input
                name="newPassword"
                type="password"
                required
                value={formData.newPassword}
                onChange={handleInputChange}
                className="block w-full px-3 py-3 border border-gray-300 rounded-lg placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                placeholder="Nuova password"
              />
            </div>
            <div>
              <input
                name="confirmNewPassword"
                type="password"
                required
                value={formData.confirmNewPassword}
                onChange={handleInputChange}
                className="block w-full px-3 py-3 border border-gray-300 rounded-lg placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                placeholder="Conferma nuova password"
              />
            </div>
          </div>

          {successMessage && (
            <div className="flex items-center text-green-600 text-sm mt-2">
              <CheckCircle className="h-4 w-4 mr-2" />
              {successMessage}
            </div>
          )}

          {errorMessage && (
            <div className="flex items-center text-red-600 text-sm mt-2">
              <AlertCircle className="h-4 w-4 mr-2" />
              {errorMessage}
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
            style={{ background: yellowGradient }}
          >
            {isLoading ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            ) : (
              'Aggiorna password'
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default UpdatePassword;